// User detail drawer + invite modal for Admin · Users. Reads canonical User
// fields (ISO timestamps) and the real audit feed; the invite modal POSTs to
// /admin/users/invite. Mutations for the drawer's actions live in the parent
// (admin-users.tsx) which passes busy/error down, and the sentence saying what
// the last action did (`notice`): the drawer stays open after an action.

import { ResponsiveOverlay } from '@/components/responsive-overlay';
import { useEffect, useId, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { Icons } from '@/components/primitives';
import { useQuery } from '@tanstack/react-query';
import { useRecentAuditEvents } from '@/api/hooks';
import { useInviteUser } from '@/api/admin-mutations';
import { useAdminRevokeCredential } from '@/api/credential-mutations';
import { adminUserCredentials } from '@/api/resources';
import { FRESHNESS } from '@/api/resource';
import { useRefreshWhenOlderThan } from '@/lib/use-refresh-when-older-than';
import { canAtLeast } from '@/auth/capabilities';
import { useMe } from '@/auth/useMe';
import { CredentialList } from '@/components/credential-list';
import { ApiError } from '@/api/errors';
import { MutationErrorLine } from '@/components/error-panel';
import { RequiredField, useRequiredFeedback } from '@/components/required-field';
import { Avatar, RoleBadge } from './admin-shared';
import type { CredentialSummary, Role, User, UserActionResponse, UserStatus } from '@/api/types';

// ── User detail drawer + Invite modal ────────────────────────────────
// Pair lives next to UsersView. Drawer for editing a single user;
// modal for inviting a new one. Both reuse existing drawer/modal CSS.

const ROLES: readonly Role[] = ['admin', 'planner', 'reporter'];

type ConfirmKind = 'disable' | 'delete';

export interface UserDetailDrawerProps {
  user: User;
  /** The loaded user list, used to resolve `invited_by` to a name. */
  users?: readonly User[];
  busy?: boolean;
  error?: unknown;
  /** What the last action did ("Role changed to Reporter."), read out as a status. */
  notice?: string | null;
  onClose: () => void;
  onSaveRole?: (userId: string, role: Role) => void;
  onDisable?: (user: User) => void;
  onEnable?: (user: User) => void;
  onResend?: (user: User) => void;
  onResetPassword?: (user: User) => void;
  onDelete?: (user: User) => void;
}

function UserDetailDrawer({ user, users = [], busy = false, error = null, notice = null, onClose, onSaveRole, onDisable, onEnable, onResend, onResetPassword, onDelete }: UserDetailDrawerProps) {
  const { data: me } = useMe();
  const isYou = me?.id === user.id;

  // Local editable role — saved via "Update role" CTA.
  const [role, setRole] = useState<Role>(user.role);
  const dirty = role !== user.role;

  // Inline-confirm popovers (same pattern as audience drawer).
  const [confirmKind, setConfirmKind] = useState<ConfirmKind | null>(null);
  useEffect(() => {
    if (!confirmKind) return undefined;
    const off = (e: MouseEvent) => {
      const target = e.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('.ad-confirm-pop') && !target.closest('.ad-confirm-trigger')) {
        setConfirmKind(null);
      }
    };
    document.addEventListener('mousedown', off);
    return () => document.removeEventListener('mousedown', off);
  }, [confirmKind]);

  // Last 8 audit entries by this user — quick activity preview, one request.
  const { data: recentActivity = [] } = useRecentAuditEvents({ actor_id: user.id }, 8);

  const invitedBy = inviterName(user.invited_by, users) ?? '—';
  const invitedAgo = user.invited_at ? fmtRelative(+new Date(user.invited_at)) : '—';

  return (
    <>

      <ResponsiveOverlay label="admin user drawer details" onClose={onClose} className="drawer">

        {/* ── Header ─────────────────────────────────────────── */}
        <div className="drawer-hd">
          <div style={{flex:1, minWidth:0, display:'flex', gap:14, alignItems:'flex-start'}}>
            <Avatar user={user} size={52}/>
            <div style={{minWidth:0}}>
              <div className="muted mono" style={{fontSize:10, letterSpacing:'.1em'}}>
                USER · {(user.status || 'active').toUpperCase()}
                {isYou && <span style={{marginLeft:8, color:'var(--accent)'}}>· YOU</span>}
              </div>
              <h2 style={{fontFamily:'var(--font-display)', fontSize:22, fontWeight:800, letterSpacing:'.01em', textTransform:'uppercase', margin:'4px 0 4px'}}>
                {user.name}
              </h2>
              <div className="mono" style={{fontSize:11.5, color:'var(--ink-1)'}}>{user.email}</div>
              <div className="flex items-center gap-3" style={{marginTop:6, flexWrap:'wrap'}}>
                <RoleBadge role={user.role}/>
                {user.twofa && <span className="tag" style={{background:'color-mix(in oklch, var(--good) 12%, transparent)', color:'var(--good)', borderColor:'color-mix(in oklch, var(--good) 35%, var(--line))'}}>2FA</span>}
                {user.sso && <span className="tag">SSO</span>}
              </div>
            </div>
          </div>
          <button className="btn btn-sm btn-ghost" aria-label="Close" onClick={onClose} style={{fontSize:18, padding:'4px 10px'}}>×</button>
        </div>

        {/* ── Body ───────────────────────────────────────────── */}
        <div className="drawer-body">

          {/* Result of the last action. The live region is always mounted so a
              screen reader announces the sentence when it appears. */}
          <div role="status">
            {notice && (
              <div style={{
                padding:'10px 12px', marginBottom:14,
                background:'color-mix(in oklch, var(--good) 12%, transparent)',
                border:'1px solid color-mix(in oklch, var(--good) 40%, var(--line))',
                borderLeft:'3px solid var(--good)',
                borderRadius:4,
                display:'flex', alignItems:'center', gap:10,
                fontSize:11.5, color:'var(--ink-1)',
              }}>
                <span style={{color:'var(--good)'}}>{Icons.check}</span>
                <span>{notice}</span>
              </div>
            )}
          </div>

          {/* Pending invite banner */}
          {user.status === 'invited' && (
            <div style={{
              padding:'10px 12px', marginBottom:14,
              background:'color-mix(in oklch, var(--warn) 12%, transparent)',
              border:'1px solid color-mix(in oklch, var(--warn) 35%, var(--line))',
              borderLeft:'3px solid var(--warn)',
              borderRadius:4,
              display:'flex', alignItems:'center', gap:10,
              fontSize:11.5, color:'var(--ink-1)',
            }}>
              <span style={{color:'var(--warn)'}}>{Icons.send}</span>
              <span>Invite sent {invitedAgo}. The user hasn’t set up their account yet.</span>
              <button className="btn btn-sm" style={{marginLeft:'auto'}} disabled={busy} onClick={() => onResend?.(user)}>Resend invite</button>
            </div>
          )}
          {user.status === 'disabled' && (
            <div style={{
              padding:'10px 12px', marginBottom:14,
              background:'var(--bg-sunk)',
              border:'1px solid var(--line)',
              borderLeft:'3px solid var(--ink-3)',
              borderRadius:4,
              display:'flex', alignItems:'center', gap:10,
              fontSize:11.5, color:'var(--ink-1)',
            }}>
              <span style={{color:'var(--ink-3)'}}>{Icons.x}</span>
              <span>This user is disabled and can’t sign in. Their history is preserved.</span>
            </div>
          )}

          {/* Role */}
          <div className="flex between items-center mb-2">
            <div className="field-lbl" style={{margin:0}}>Role</div>
            <Link
              to="/admin/roles"
              style={{fontFamily:'var(--font-mono)', fontSize:10, letterSpacing:'.1em', textTransform:'uppercase', color:'var(--ink-3)', textDecoration:'none'}}
            >
              See what each role can do →
            </Link>
          </div>
          <div className="pair-phone" style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:8, marginBottom:14}}>
            {ROLES.map(r => (
              <button
                key={r}
                onClick={() => setRole(r)}
                aria-pressed={role === r}
                disabled={user.status === 'disabled' || busy}
                className="role-picker"
                style={{
                  textAlign:'left', cursor:'pointer',
                  padding:'12px 14px',
                  border: '1px solid ' + (role === r ? 'var(--accent)' : 'var(--line)'),
                  borderRadius:'var(--r)',
                  background: role === r ? 'color-mix(in oklch, var(--accent) 8%, var(--panel))' : 'var(--panel)',
                  color:'var(--ink)',
                  opacity: user.status === 'disabled' ? 0.5 : 1,
                }}
              >
                <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4}}>
                  <RoleBadge role={r}/>
                  {role === r && <span style={{color:'var(--accent)', fontSize:14}}>{Icons.check}</span>}
                </div>
                <div style={{fontSize:11, color:'var(--ink-2)', lineHeight:1.35}}>
                  {ROLE_BLURB[r]}
                </div>
              </button>
            ))}
          </div>
          {dirty && (
            <div style={{display:'flex', gap:8, marginBottom:14, alignItems:'center'}}>
              <span className="mono" style={{fontSize:10.5, color:'var(--ink-2)', letterSpacing:'.08em'}}>
                CHANGE: {user.role} → {role}
              </span>
              <button className="btn btn-sm" style={{marginLeft:'auto'}} disabled={busy} onClick={() => setRole(user.role)}>Cancel</button>
              <button className="btn btn-primary btn-sm" disabled={busy} onClick={() => onSaveRole?.(user.id, role)}>
                {Icons.check} {busy ? 'Saving…' : 'Update role'}
              </button>
            </div>
          )}

          {/* Production scope (planner only) */}
          {user.role === 'planner' && user.productions && (
            <>
              <div className="field-lbl mb-2">Production scope</div>
              <div className="panel" style={{padding:12, marginBottom:14}}>
                <div style={{fontSize:11.5, color:'var(--ink-2)', marginBottom:6}}>
                  Restricted to {user.productions.length} production{user.productions.length === 1 ? '' : 's'}. Won’t see campaigns or audiences for productions outside this list.
                </div>
                <div className="flex" style={{flexWrap:'wrap', gap:4}}>
                  {user.productions.map(p => (
                    <span key={p} className="tag" style={{textTransform:'uppercase', letterSpacing:'.04em'}}>{p}</span>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Account info */}
          <div className="field-lbl mb-2">Account</div>
          <div className="panel" style={{padding:'4px 12px', marginBottom:14}}>
            {([
              ['ID',            user.id],
              ['Email',         user.email],
              ['Sign-in',       user.sso ? 'SSO (SAML · Okta)' : 'Password'],
              ['2FA',           user.twofa ? 'Enabled' : 'Not enabled'],
              ['Invited',       invitedAgo],
              ['Invited by',    invitedBy],
              ['Last sign-in',  user.last_seen ? fmtRelative(+new Date(user.last_seen)) : 'Never'],
            ] satisfies readonly (readonly [string, string])[]).map(([k, v]) => (
              <div key={k} style={{
                display:'grid', gridTemplateColumns:'120px 1fr',
                padding:'8px 0', borderBottom:'1px solid var(--line-2)',
                fontSize:12,
              }}>
                <div className="muted mono" style={{fontSize:10.5, letterSpacing:'.06em', textTransform:'uppercase'}}>{k}</div>
                <div>{v}</div>
              </div>
            ))}
          </div>

          {/* API keys and connected apps: unscoped admins only, as the API allows. */}
          {me && canAtLeast(me.role, 'tokens_revoke', 'full') && me.productions == null && (
            <UserApiAccess user={user}/>
          )}

          {/* Recent activity */}
          <div className="flex between items-center mb-2">
            <div className="field-lbl" style={{margin:0}}>Recent activity</div>
            <span className="muted mono" style={{fontSize:10, letterSpacing:'.06em'}}>
              (last {recentActivity.length})
            </span>
          </div>
          <div className="panel" style={{padding:'4px 0', marginBottom:8}}>
            {recentActivity.length === 0 ? (
              <div className="muted" style={{padding:'14px 16px', fontSize:12, fontStyle:'italic'}}>
                No recent activity.
              </div>
            ) : recentActivity.map((e, i) => (
              <div key={e.id} style={{
                display:'grid', gridTemplateColumns:'1fr auto',
                gap:10, padding:'8px 14px',
                borderTop: i > 0 ? '1px solid var(--line-2)' : 'none',
                fontSize:12, alignItems:'center',
              }}>
                <div style={{minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap'}}>
                  <span style={{color:'var(--ink-2)'}}>{e.verb}</span>{' '}
                  <span style={{color:'var(--ink)', fontWeight:600}}>{e.target?.name || e.target?.id}</span>
                </div>
                <div className="muted mono" style={{fontSize:10, letterSpacing:'.04em'}}>{fmtRelative(+new Date(e.ts))}</div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Footer ──────────────────────────────────────────── */}
        {error ? (
          <div style={{padding:'8px 16px 0'}}>
            <MutationErrorLine error={error}/>
          </div>
        ) : null}
        <div className="drawer-foot" style={{position:'relative'}}>
          {user.status === 'invited' && (
            <button className="btn btn-sm" disabled={busy} onClick={() => onResend?.(user)}>{Icons.send} Resend invite</button>
          )}
          {user.status === 'active' && (
            <button
              className="btn btn-sm ad-confirm-trigger"
              onClick={() => setConfirmKind(confirmKind === 'disable' ? null : 'disable')}
              disabled={isYou || busy}
              title={isYou ? 'You can’t disable yourself' : 'Disable this user'}
            >Disable</button>
          )}
          {user.status === 'active' && (
            <button
              className="btn btn-sm"
              onClick={() => onResetPassword?.(user)}
              disabled={user.sso || busy}
              title={user.sso
                ? 'Identity is managed by SSO — reset password in your identity provider'
                : 'Issue a password-reset link (1-hour expiry)'}
            >Reset password</button>
          )}
          {user.status === 'disabled' && (
            <button className="btn btn-sm" disabled={busy} onClick={() => onEnable?.(user)}>Re-enable</button>
          )}
          <button
            className="btn btn-sm ad-confirm-trigger"
            onClick={() => setConfirmKind(confirmKind === 'delete' ? null : 'delete')}
            disabled={isYou || busy}
            style={{color: isYou ? undefined : 'var(--bad)'}}
            title={isYou ? 'You can’t delete yourself' : 'Permanently delete this user'}
          >{Icons.trash} Delete</button>

          {confirmKind === 'disable' && (
            <div className="ad-confirm-pop" style={{left:96}}>
              <div className="ad-confirm-arrow"/>
              <div className="ad-confirm-title">Disable this user?</div>
              <div className="ad-confirm-body">
                <b>{user.name}</b> will be signed out everywhere and won’t be able to sign in again until you re-enable them. Their work history stays intact.
              </div>
              <div className="ad-confirm-actions">
                <button className="btn btn-sm btn-ghost" onClick={() => setConfirmKind(null)}>Cancel</button>
                <button className="btn btn-sm ad-confirm-go" disabled={busy} onClick={() => { setConfirmKind(null); onDisable?.(user); }}>Disable</button>
              </div>
            </div>
          )}
          {confirmKind === 'delete' && (
            <div className="ad-confirm-pop" style={{left:96}}>
              <div className="ad-confirm-arrow"/>
              <div className="ad-confirm-title">Delete this user?</div>
              <div className="ad-confirm-body">
                <b>{user.name}</b> will be removed from the org. Their audit history is preserved but they’ll show as <i>deleted user</i> on past entries. <b>Not reversible.</b>
              </div>
              <div className="ad-confirm-actions">
                <button className="btn btn-sm btn-ghost" onClick={() => setConfirmKind(null)}>Cancel</button>
                <button className="btn btn-sm ad-confirm-go" disabled={busy} onClick={() => { setConfirmKind(null); onDelete?.(user); }}>Delete</button>
              </div>
            </div>
          )}
        </div>
      </ResponsiveOverlay>
    </>
  );
}

// ── API access (the user's API keys and connected apps) ──────────────
// Read from `GET /admin/users/{id}/credentials`, which only an admin with no production
// scope may call; the drawer draws nothing for anyone else. Revoking asks first, in place.
function UserApiAccess({ user }: { user: User }) {
  const headingId = useId();
  const query = useQuery(adminUserCredentials(user.id));
  // "Last used" moves without a revision; never show it more than a minute old on arrival.
  useRefreshWhenOlderThan(query, FRESHNESS.minute);
  const revoke = useAdminRevokeCredential();
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<{ id: string; error: unknown } | null>(null);
  const data = query.data;
  const onRevoke = (credential: CredentialSummary) => {
    setRevokingId(credential.id);
    setRevokeError(null);
    revoke.mutate(
      { userId: user.id, credentialId: credential.id },
      {
        onError: (error) => setRevokeError({ id: credential.id, error }),
        onSettled: () => setRevokingId(null),
      },
    );
  };
  return (
    <section aria-label="API access" style={{marginBottom:14}}>
      <div className="field-lbl mb-2" id={headingId} tabIndex={-1}>API access</div>
      <div className="muted" style={{fontSize:11.5, lineHeight:1.5, marginBottom:8}}>
        API keys and connected apps act as {user.name}, never beyond their role.
      </div>
      {query.isError && (
        <div style={{marginBottom:8}}><MutationErrorLine error={query.error}/></div>
      )}
      {query.isPending && (
        <div className="muted" style={{fontSize:12, fontStyle:'italic'}}>Loading API access…</div>
      )}
      {data && (
        <>
          {(!data.api_keys_enabled || !data.mcp_enabled) && (
            <div className="muted" style={{fontSize:11.5, marginBottom:8}}>
              {!data.api_keys_enabled && !data.mcp_enabled
                ? 'API keys and connected apps are switched off for everyone; nothing below is accepted until they are switched back on.'
                : !data.api_keys_enabled
                  ? 'API keys are switched off for everyone; no key below is accepted until they are switched back on.'
                  : 'Connected apps are switched off for everyone; no connection below is accepted until they are switched back on.'}
            </div>
          )}
          <CredentialList
            credentials={data.credentials}
            label={`API access of ${user.name}`}
            focusFallbackId={headingId}
            viewer="admin"
            empty="No API keys or connected apps."
            onRevoke={onRevoke}
            revokingId={revokingId}
            revokeError={revokeError}
          />
        </>
      )}
    </section>
  );
}

// ── New User Modal (invite by email) ──────────────────────────────────
// POSTs /admin/users/invite; on success hands the UserActionResponse (user +
// one-time action_link) to the parent, which shows the flash banner.
export interface NewUserModalProps {
  open: boolean;
  onClose: () => void;
  onInvited?: (response: UserActionResponse) => void;
  /** The loaded user list, for the client-side duplicate-email check. */
  existing?: readonly User[];
}

function NewUserModal({ open, onClose, onInvited, existing }: NewUserModalProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<Role>('planner');
  const emailId = useId();
  const nameId = useId();
  const invite = useInviteUser();
  const required = useRequiredFeedback();

  useEffect(() => {
    if (open) {
      setEmail('');
      setName('');
      setRole('planner');
      required.clearAll();
      invite.reset();
    }
  }, [open]);

  if (!open) return null;
  const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
  const emailExists = existing?.some(u => u.email.toLowerCase() === email.trim().toLowerCase());
  // The server double-checks uniqueness — surface its 409 on the email field
  // (covers users the cached list hasn't seen yet).
  const serverConflict = invite.error instanceof ApiError && invite.error.status === 409;
  const emailIssue = !email.trim()
    ? 'Enter an email address.'
    : !emailValid
      ? 'That doesn’t look like an email address.'
      : (emailExists || serverConflict)
        ? 'A user with this email already exists.'
        : null;
  const nameIssue = name.trim().length <= 1
    ? 'Enter the user’s full name — at least 2 characters.'
    : null;

  const submit = () => {
    if (invite.isPending) return;
    const canSubmit = required.report([
      emailIssue && { key: 'invite-email', message: emailIssue },
      nameIssue && { key: 'invite-name', message: nameIssue },
    ]);
    if (!canSubmit) return;
    invite.mutate(
      { email: email.trim().toLowerCase(), name: name.trim(), role },
      { onSuccess: (r) => onInvited?.(r) },
    );
  };

  return (
    <ResponsiveOverlay label="admin user drawer" kind="form" onClose={onClose} className="modal-host">
      <div className="modal-card" onClick={e => e.stopPropagation()} style={{maxWidth:520}}>
        <div className="modal-hd">
          <div>
            <div className="modal-title">Invite user</div>
            <div className="muted" style={{fontSize:11.5, marginTop:2}}>
              They’ll get an email with a 7-day invite link. They set their own password when they accept.
            </div>
          </div>
          <button className="modal-x" aria-label="Close" onClick={onClose}>×</button>
        </div>
        <div className="modal-body">
          <RequiredField
            fieldKey="invite-email"
            message={required.messages['invite-email'] || (serverConflict ? emailIssue : null)}
            attempt={required.attempt}
            className="field"
            style={{marginBottom:12}}
          >
            <label className="field-lbl" htmlFor={emailId}>Email</label>
            <input
              id={emailId}
              className="input mono"
              style={{fontSize:12.5}}
              autoFocus
              placeholder="user@shubert.com"
              value={email}
              onChange={e => {
                const next = e.target.value;
                setEmail(next);
                invite.reset();
                const nextValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(next.trim());
                const nextExists = existing?.some(
                  u => u.email.toLowerCase() === next.trim().toLowerCase(),
                );
                if (nextValid && !nextExists) required.clear('invite-email');
              }}
            />
            {email && !emailValid && !required.messages['invite-email'] && (
              <div className="field-hint" style={{color:'var(--bad)'}}>That doesn’t look like an email address.</div>
            )}
            {emailValid && (emailExists || serverConflict) && !required.messages['invite-email'] && !serverConflict && (
              <div className="field-hint" style={{color:'var(--bad)'}}>A user with this email already exists.</div>
            )}
          </RequiredField>
          <RequiredField
            fieldKey="invite-name"
            message={required.messages['invite-name']}
            attempt={required.attempt}
            className="field"
            style={{marginBottom:14}}
          >
            <label className="field-lbl" htmlFor={nameId}>Full name</label>
            <input
              id={nameId}
              className="input"
              placeholder="Maya Chen"
              value={name}
              onChange={e => {
                const next = e.target.value;
                setName(next);
                if (next.trim().length > 1) required.clear('invite-name');
              }}
            />
          </RequiredField>
          <div className="field" style={{marginBottom:4}}>
            <label className="field-lbl">Role</label>
            <div className="pair-phone" style={{display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:8, marginTop:4}}>
              {ROLES.map(r => (
                <button
                  key={r}
                  onClick={() => setRole(r)}
                  aria-pressed={role === r}
                  style={{
                    textAlign:'left', cursor:'pointer',
                    padding:'12px 14px',
                    border: '1px solid ' + (role === r ? 'var(--accent)' : 'var(--line)'),
                    borderRadius:'var(--r)',
                    background: role === r ? 'color-mix(in oklch, var(--accent) 8%, var(--panel))' : 'var(--panel)',
                    color:'var(--ink)',
                  }}
                >
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:4}}>
                    <RoleBadge role={r}/>
                    {role === r && <span style={{color:'var(--accent)', fontSize:13}}>{Icons.check}</span>}
                  </div>
                  <div style={{fontSize:11, color:'var(--ink-2)', lineHeight:1.35}}>
                    {ROLE_BLURB[r]}
                  </div>
                </button>
              ))}
            </div>
          </div>
          {invite.error && !serverConflict && (
            <div style={{marginTop:10}}>
              <MutationErrorLine error={invite.error}/>
            </div>
          )}
        </div>
        <div className="modal-foot">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" disabled={invite.isPending} onClick={submit}>
            {Icons.send} {invite.isPending ? 'Sending…' : 'Send invite'}
          </button>
        </div>
      </div>
    </ResponsiveOverlay>
  );
}

// ── Helpers ───────────────────────────────────────────────────────────
/** The words the filters, the status line and the CSV export use for a role. */
/**
 * The name for an `invited_by` user id, or null when nobody invited them. The
 * directory is the whole user list, so an id missing from it is a user since
 * deleted: say so rather than show the id.
 */
function inviterName(invitedBy: string | null, users: readonly Pick<User, 'id' | 'name'>[]): string | null {
  if (!invitedBy) return null;
  return users.find(u => u.id === invitedBy)?.name || 'Unknown user';
}

const ROLE_LABEL: Record<Role, string> = {
  admin:    'Admin',
  planner:  'Planner',
  reporter: 'Reporter',
};

/** The words the filters and the CSV export use for a user status. */
const STATUS_LABEL: Record<UserStatus, string> = {
  active:   'Active',
  invited:  'Invited',
  disabled: 'Disabled',
};

const ROLE_BLURB: Record<Role, string> = {
  admin:    'Full access. Manages users & integrations.',
  planner:  'Run campaigns. Read-write on day-to-day work.',
  reporter: 'View-only stakeholder access. Can export reports.',
};

function fmtRelative(ts: number): string {
  const ms = Date.now() - ts;
  if (!Number.isFinite(ms)) return '—';
  const s = ms / 1000;
  if (s < 60)  return Math.max(1, Math.round(s)) + 's ago';
  if (s < 3600) return Math.round(s / 60) + 'm ago';
  if (s < 86400) return Math.round(s / 3600) + 'h ago';
  return Math.round(s / 86400) + 'd ago';
}

export { UserDetailDrawer, NewUserModal, fmtRelative, inviterName, ROLE_LABEL, STATUS_LABEL };
